import { Grid } from '../core/Grid.js';
import { PersistenceManager } from '../persistence/PersistenceManager.js';
import { GAME_TYPES, VortexBackup, SavedGrid } from '../persistence/types.js';
import { scoreGameState, isVortexHubValid } from '../games/GameScoring.js';
import { VortexHub } from './VortexHub.js';

/**
 * A state's quality along the three dimensions that order Vortex progress,
 * best-first: more wins, then a valid Hub, then a lower total score.
 */
interface StateSummary {
    winCount: number;
    totalScore: number;
    hubValid: boolean;
}

/**
 * Live App state the backup logic reads. Passed as accessors (not snapshots) so
 * the manager always sees the current grid/hub without App having to push updates.
 */
export interface VortexBackupContext {
    readonly persistence: PersistenceManager;
    getGridId(): string | null;
    getGrid(): Grid | null;
    getHub(): VortexHub | null;
}

/**
 * Owns the Vortex backup subsystem: snapshotting wins + Hub state, scoring a
 * snapshot, deciding when a candidate beats the saved one, and detecting whether
 * the saved backup still differs from the live board. App keeps the UI/lifecycle
 * handlers (the Restore-backup button + its click handler) and calls in here for
 * the computation, so the scoring/snapshot rules live in one testable place.
 *
 * First slice of the VortexController extraction — the most self-contained part.
 */
export class VortexBackupManager {
    constructor(private readonly ctx: VortexBackupContext) {}

    /**
     * Recompute a backup's win count, total score and Hub validity, everything
     * scored under the backup's own Hub. Hub validity is part of the summary
     * because a valid Hub is strictly better than an invalid one at the same win
     * count — a completed grid requires it (see docs/vortex.md § Validation).
     */
    summary(backup: VortexBackup): StateSummary {
        return this.scoreState(backup.hubState, backup.winStates);
    }

    /**
     * Summary of the live board: each game's saved best state scored under the
     * live Hub. Games whose best state no longer validates against the live Hub
     * (e.g. after a gate removal) simply don't count as wins.
     */
    currentSummary(grid: SavedGrid): StateSummary {
        const hub = this.ctx.getHub();
        const winStates: VortexBackup['winStates'] = {};
        for (const gameType of GAME_TYPES) {
            const best = grid.gameStates[gameType]?.bestSerialized;
            if (best) winStates[gameType] = best;
        }
        return this.scoreState(hub ? hub.serialize() : '', winStates);
    }

    /** Score a set of win states under a given Hub, and record that Hub's validity. */
    private scoreState(hubState: string, winStates: VortexBackup['winStates']): StateSummary {
        let winCount = 0;
        let totalScore = 0;
        let hubValid = false;
        const grid = this.ctx.getGrid();
        if (grid) {
            for (const gameType of GAME_TYPES) {
                const serialized = winStates[gameType];
                if (!serialized) continue;
                const { score } = scoreGameState(grid, gameType, serialized, hubState);
                if (score) { winCount++; totalScore += score.totalScore; }
            }
            hubValid = isVortexHubValid(grid, hubState);
        }
        return { winCount, totalScore, hubValid };
    }

    /** Snapshot all current wins + Hub state as a backup, if better than the existing one. */
    maybeCreate(): void {
        const gridId = this.ctx.getGridId();
        if (!gridId || !this.ctx.getHub()) return;
        const grid = this.ctx.persistence.getGrid(gridId);
        if (!grid) return;

        const backup = this.buildSnapshot(grid);
        if (!backup) return;

        const existing = this.ctx.persistence.getVortexBackup(gridId);
        if (!existing || this.isBetter(backup, existing)) {
            this.ctx.persistence.saveVortexBackup(gridId, backup);
        }
    }

    /** After a game win on a Vortex grid, update the backup if current state is better. */
    maybeUpdate(): void {
        const gridId = this.ctx.getGridId();
        if (!gridId || !this.ctx.getHub()) return;
        const existing = this.ctx.persistence.getVortexBackup(gridId);
        if (!existing) return; // Only update if a backup already exists

        const grid = this.ctx.persistence.getGrid(gridId);
        if (!grid) return;

        const current = this.buildSnapshot(grid);
        if (!current) return;

        if (this.isBetter(current, existing)) {
            this.ctx.persistence.saveVortexBackup(gridId, current);
        }
    }

    /**
     * Should the "Restore backup" button be offered? Only when restoring is an
     * actual gain: the backup differs from the live board *and* is strictly
     * better than it. Without the strict-better gate, a live board that has since
     * been improved (e.g. its Hub re-linked to valid) would be offered a downgrade
     * back to the backup at the same win count/score — the bug this guards against.
     */
    shouldOfferRestore(grid: SavedGrid, backup: VortexBackup): boolean {
        if (!this.differsFromCurrent(grid, backup)) return false;
        return this.compare(this.summary(backup), this.currentSummary(grid)) > 0;
    }

    /** Whether the saved backup differs from the current Hub + won-game states. */
    differsFromCurrent(grid: SavedGrid, backup: VortexBackup): boolean {
        const hub = this.ctx.getHub();
        // Hub state — compared canonically, so a link rebuilt in the other direction
        // (or pairs in a different order) doesn't count as a change.
        if (hub && VortexHub.canonical(hub.serialize()) !== VortexHub.canonical(backup.hubState)) {
            return true;
        }
        for (const gameType of GAME_TYPES) {
            const backupWin = backup.winStates[gameType];
            if (!backupWin) continue;
            if (grid.gameStates[gameType]?.bestSerialized !== backupWin) return true;
        }
        return false;
    }

    /** A backup is better if it ranks above the existing one on the state order. */
    private isBetter(candidate: VortexBackup, existing: VortexBackup): boolean {
        return this.compare(this.summary(candidate), this.summary(existing)) > 0;
    }

    /**
     * Total order over states, best-first: more wins, then a valid Hub, then a
     * lower total score. Returns > 0 if `a` is better than `b`, < 0 if worse, 0 if
     * equal on all three dimensions.
     */
    private compare(a: StateSummary, b: StateSummary): number {
        if (a.winCount !== b.winCount) return a.winCount - b.winCount;
        if (a.hubValid !== b.hubValid) return a.hubValid ? 1 : -1;
        return b.totalScore - a.totalScore; // lower score is better
    }

    private buildSnapshot(grid: SavedGrid): VortexBackup | null {
        const hub = this.ctx.getHub();
        if (!hub) return null;

        const winStates: VortexBackup['winStates'] = {};
        for (const gameType of GAME_TYPES) {
            const best = grid.gameStates[gameType]?.bestSerialized;
            if (best) winStates[gameType] = best;  // bestSerialized ⟹ a won state
        }

        if (Object.keys(winStates).length === 0) return null;

        return { hubState: hub.serialize(), winStates };
    }
}
